import cx from 'classnames';
import { CSSProperties, PropsWithChildren, useEffect, useRef, useState } from 'react';

import { Button, ButtonProps } from '../form/button/Button';
import { IconChevronDown } from '../icons/ChevronDown';
import { IconChevronUp } from '../icons/ChevronUp';

import style from './ReadMore.module.css';

type Props = PropsWithChildren<{
    className?: string;
    lineClamp: number;
    onToggle?: (isOpen: boolean) => void;
    readLess: string;
    readMore: string;
    buttonProps?: Pick<ButtonProps, 'reversed'>;
}>;

export function ReadMore(props: Props) {
    const { children, lineClamp, onToggle, readMore, readLess, buttonProps } = props;
    const textRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [hasMoreText, setHasMoreText] = useState(true);
    const [maxHeight, setMaxHeight] = useState<'unset' | number>('unset');

    useEffect(() => {
        function updateMaxHeight() {
            setMaxHeight(textRef.current?.scrollHeight ?? 'unset');
        }
        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        return () => {
            window.removeEventListener('resize', updateMaxHeight);
        };
    }, [isOpen]);

    function handleButtonClick() {
        setIsOpen((isOpen) => {
            const isOpenToggled = !isOpen;
            if (onToggle) {
                onToggle(isOpenToggled);
            }
            return isOpenToggled;
        });
    }

    useEffect(() => {
        if (textRef.current === null) {
            return;
        }
        setHasMoreText(textRef.current.clientHeight < textRef.current.scrollHeight);
    }, []);

    useEffect(() => {
        if (!hasMoreText) {
            setIsOpen(true);
        }
    }, [hasMoreText]);

    return (
        <>
            <div
                ref={textRef}
                className={cx(style.text, props.className, {
                    [style.isOpen]: isOpen,
                })}
                // eslint-disable-next-line react/forbid-dom-props
                style={
                    {
                        '--line-clamp': lineClamp.toString(),
                        maxHeight: isOpen ? maxHeight : undefined, // `undefined` to not override the CSS module value
                    } as CSSProperties
                }
            >
                {children}
            </div>
            {hasMoreText ? (
                <Button
                    {...buttonProps}
                    priority="text"
                    iconPosition="right"
                    onClick={handleButtonClick}
                    type="button"
                    Icon={isOpen ? IconChevronUp : IconChevronDown}
                >
                    {isOpen ? readLess : readMore}
                </Button>
            ) : null}
        </>
    );
}
